import moment from 'moment';
import 'moment/dist/locale/es';
import {useState} from 'react';
import {useSelector} from 'react-redux';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {useLocale} from '../context/locale-context';
import {CancelButton, RescheduleButton} from './buttons';
import CancelModal from './cancel-modal';
import RescheduleModal from './reschedule-modal';
import SchedulingMessageModal from './scheduling-message-modal';
import {CalendarSVG, LocationSVG, ComeToYouSVG, DeviceSVG} from './svgs';
import {AddToCalendarButton} from 'add-to-calendar-button-react';

// todo, lazy lodal the modals without the page flashing when they are opened

export const AppointmentSummary = () => {
  const {locale} = useLocale();
  moment.locale(locale);

  const scheduler = useSelector((state) => state.scheduler);
  const {dynamicContent, repair, group} = useRepair();
  const {
    tracking,
    tracking: {fixtSupportEmail},
    cancelButton,
    scheduleButton,
    rescheduleButton,
    serviceOptions,
  } = usePageText();
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [schedulingMessageModalOpen, setSchedulingMessageModalOpen] = useState(false);
  const {state, scheduledWindowStart, storeName} = repair;
  const startTime = moment.utc(scheduledWindowStart).local();
  const endTime = startTime.clone().add(1, 'hour');

  const calendar = moment({
    calendar: {
      sameDay: '[Today] [at] LT z',
      nextDay: '[Tomorrow] [at] LT z',
      lastWeek: 'ddd, LL LT, z',
      nextWeek: 'ddd, LL LT, z',
      sameElse: 'ddd, LL LT, z',
    },
  });

  const cancelEnabled = () => {
    if (scheduler.loading) return false; //disable if loading
    if (!repair.storeLookupCode) return true; //enable if not in store
    if (group.inStoreCancelEnabled) return true; //enable if in store and in store cancel is enabled

    return false;
  };

  const rescheduleEnabled = () => {
    if (scheduler.loading) return false; //disable if loading
    if (repair.isPending) return true; //enable if pending
    if (!repair.storeLookupCode) return true; //enable if not in store
    if (group.inStoreRescheduleEnabled) return true; //enable if in store and in store reschedule is enabled via group

    return false;
  };

  const repairHasBegun = (repairState) => {
    return repairState === 'repair_scheduled' && moment() > startTime;
  };
  const isBeforeCloseOfCancellationWindow = (startTime) => {
    if (!startTime) return true;

    return startTime > moment().add(group.cancellationWindowInHours, 'hours');
  };

  const formatLocation = (address) => (storeName ? `${storeName} - ${address}` : address);

  const renderCancellationDisclosure = () => {
    if (repair.isPending || (group.cancellationWindowInHours < 1 && rescheduleEnabled() && cancelEnabled())) {
      return null;
    } else if (['completed', 'canceled'].includes(state)) {
      return null;
    } else if (repairHasBegun(state, startTime)) {
      return null;
    } else if (!isBeforeCloseOfCancellationWindow(startTime) || !rescheduleEnabled() || !cancelEnabled()) {
      return <p>{dynamicContent.cancelTooClose}</p>;
    } else {
      return (
        <p data-testid="cancel-time">
          {tracking.cancellationTime.p1}
          <span className="text-red">
            {` ${moment(startTime)
              .clone()
              .subtract(group.cancellationWindowInHours, 'hours')
              .calendar(null, calendar)} `}
          </span>
          {tracking.cancellationTime.p2}
        </p>
      );
    }
  };

  const {
    address,
    device,
    productBrandName,
    translatedColor,
    translatedIssues,
    isPending,
    issuesDescription,
    storeLookupCode,
    googlePlaceId,
    timeZone,
  } = repair;
  const {schedulingEnabled} = group;
  const formattedDate = startTime.format('dddd, MMMM Do, h:mm A');

  const {clickText, schedule, bookText} = tracking.repairSummaryPending;

  const hideButtons = ['completed', 'in_progress', 'canceled'].includes(state);
  const sendCancelEmail = () => {
    return (window.location = `mailto:${fixtSupportEmail}?subject=Cancel repair ${
      repair.id
    }&body=I would like to cancel my repair for ${startTime.format('MMMM Do YYYY, h:mm a')}`);
  };
  const sendRescheduleEmail = () => {
    return (window.location = `mailto:${fixtSupportEmail}?subject=Reschedule repair ${
      repair.id
    }&body=I would like to reschedule my repair from ${startTime.format('MMMM Do YYYY, h:mm a')} to ...`);
  };

  // TODO: additional formatting needed to handle international numbers
  const formattedNumber = (storeNumber) => {
    if (typeof storeNumber !== 'string') return storeNumber;

    const digits = storeNumber.replace(/\D/g, '');
    let nationalNumber = '';
    if (digits.length === 11 && digits.startsWith('1')) {
      nationalNumber = digits.slice(1);
    } else if (digits.length === 10) {
      nationalNumber = digits;
    } else {
      return storeNumber;
    }

    const area = nationalNumber.slice(0, 3);
    const mid = nationalNumber.slice(3, 6);
    const end = nationalNumber.slice(6, 10);

    return `+1 (${area}) ${mid}-${end}`;
  };

  const addressHref = googlePlaceId
    ? `https://www.google.com/maps/place/?q=place_id:${googlePlaceId}`
    : `https://www.google.com/maps/search/${address}`;

  const shouldShowCalendarButton = startTime.isValid() && address && dynamicContent.showAddToCalendarButton;

  return (
    <div className="flex flex-col gap-4 text-gray" data-testid="repair-info">
      <div className="flex flex-col bg-light-blue p-4 rounded-lg">
        <div className="py-2">
          <span className="flex justify-center">
            <div className="text-center">
              <h4 className="text-black font-bold text-lg" data-testid="device-info">
                {[productBrandName, device, translatedColor].join(' ')}
              </h4>
              <h4 className="whitespace-pre-wrap" data-testid="device-issues">
                {translatedIssues?.map((issue) => issue.replace(/([a-z](?=[A-Z]))/g, '$1 ')).join('  |  ')}
              </h4>
              <p className="text-sm" data-testid="issues-text">
                {issuesDescription}
              </p>
            </div>
          </span>
        </div>

        <hr className="border-light-gray py-2" />

        <span className="flex justify-center pb-4">
          <div>
            <h4 className="text-black font-bold text-lg text-center" data-testid="service-option">
              {storeLookupCode ? serviceOptions.repairShop : serviceOptions.comeToYou}
            </h4>
          </div>
        </span>

        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center shrink-0" data-testid="location">
              {storeLookupCode ? <LocationSVG /> : <ComeToYouSVG />}
            </div>
            <a
              className="underline hover:text-blue leading-tight"
              data-testid="address"
              target="_blank"
              href={addressHref}
              rel="noreferrer">
              {address}
            </a>
          </div>

          {repair.storeNumber && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center shrink-0" data-testid="number">
                <DeviceSVG />
              </div>
              <a
                className="underline hover:text-blue leading-tight"
                data-testid="store-number"
                href={`tel:${repair.storeNumber}`}>
                {formattedNumber(repair.storeNumber)}
              </a>
            </div>
          )}

          {!['canceled', 'completed'].includes(state) && (
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 flex items-center justify-center shrink-0" data-testid="calendar">
                <CalendarSVG />
              </div>
              {isPending ? (
                <p className="leading-tight" data-testid="schedule">
                  {clickText} <b>{schedule}</b> {bookText}
                </p>
              ) : (
                <h4 className="leading-tight" data-testid="request-scheduled">
                  {formattedDate} {moment().tz(timeZone).format('z')}
                </h4>
              )}
            </div>
          )}
        </div>
        {shouldShowCalendarButton ? (
          <div className="mt-4 z-0" data-testid="add-to-calendar">
            <AddToCalendarButton
              hideBranding
              hideCheckmark
              hideBackground
              iCalFileName="Assurant Repair Appointment"
              label={tracking.addToCalendar}
              options={['Google', 'Apple', 'iCal']}
              name={'Assurant Repair Appointment'}
              description={`Click to view your Assurant Repair Appointment details: ${window.location.href}`}
              startDate={startTime.format('YYYY-MM-DD')}
              endDate={endTime.format('YYYY-MM-DD')}
              startTime={startTime.format('HH:mm')}
              endTime={endTime.format('HH:mm')}
              timeZone={Intl.DateTimeFormat().resolvedOptions().timeZone}
              size="1"
              location={formatLocation(address)}
              styleLight={`
                --btn-background: #ffffff;
                --btn-text: #06c;
                --btn-border: #06c;
                --btn-hover-background: #ffffff;
                --btn-hover-border: #06c;
                --btn-hover-text: #06c;
                --btn-shadow: none;
                --btn-hover-shadow: none;

              `}
            />
          </div>
        ) : null}
      </div>

      {!hideButtons ? (
        <div className="flex gap-2" data-testid="cancel-resched">
          {cancelEnabled() ? (
            <CancelButton
              disabled={false}
              onClick={() => {
                if (!schedulingEnabled) {
                  setSchedulingMessageModalOpen(true);
                } else {
                  return isBeforeCloseOfCancellationWindow(startTime) || isPending
                    ? setCancelModalOpen(true)
                    : sendCancelEmail();
                }
              }}>
              {cancelButton}
            </CancelButton>
          ) : null}
          {rescheduleEnabled() ? (
            <RescheduleButton
              id={'btn-reschedule'}
              disabled={false}
              onClick={() => {
                if (!schedulingEnabled) {
                  setSchedulingMessageModalOpen(true);
                } else {
                  return isBeforeCloseOfCancellationWindow(startTime) || isPending
                    ? setRescheduleModalOpen(true)
                    : sendRescheduleEmail();
                }
              }}>
              {isPending ? scheduleButton : rescheduleButton}
            </RescheduleButton>
          ) : null}
        </div>
      ) : null}

      {/* Cancel/reschedule disclosure */}
      <div className="text-center" data-testid="disclosure">
        {renderCancellationDisclosure()}
      </div>

      <CancelModal modalOpen={cancelModalOpen} setModalOpen={setCancelModalOpen} />
      <RescheduleModal modalOpen={rescheduleModalOpen} setModalOpen={setRescheduleModalOpen} />

      {/* if scheduling is disabled, show the scheduling message modal */}
      <SchedulingMessageModal modalOpen={schedulingMessageModalOpen} setModalOpen={setSchedulingMessageModalOpen} />
    </div>
  );
};
